import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { getTagDescription, getTagShortLabel } from './eventTagUtils';

export interface EventTagDisplay {
  id: string;
  label: string;
  shortLabel?: string;
  description?: string;
  type?: 'system' | 'manual';
  color?: string;
  backgroundColor?: string;
}

interface EventTagChipsProps {
  tags?: EventTagDisplay[];
  size?: 'small' | 'medium';
  max?: number;
  overlay?: boolean;
  /** Usa rótulos curtos nos cards (ex.: "Encerrado" em vez de "Evento encerrado"). */
  compact?: boolean;
}

const TAG_ICONS: Record<string, React.ReactElement> = {
  em_alta: <LocalFireDepartmentIcon sx={{ fontSize: 'inherit !important' }} />,
  ingressos_esgotados: <ConfirmationNumberIcon sx={{ fontSize: 'inherit !important' }} />,
  evento_encerrado: <EventBusyIcon sx={{ fontSize: 'inherit !important' }} />,
};

function TagChip({
  tag,
  size,
  compact,
  overlay,
}: {
  tag: EventTagDisplay;
  size: 'small' | 'medium';
  compact: boolean;
  overlay: boolean;
}) {
  const label = compact ? (tag.shortLabel ?? getTagShortLabel(tag)) : tag.label;
  const description = tag.description ?? getTagDescription(tag);
  const icon = TAG_ICONS[tag.id] ?? <LocalOfferIcon sx={{ fontSize: 'inherit !important' }} />;

  const chip = (
    <Chip
      icon={icon}
      label={label}
      size={size}
      sx={{
        fontWeight: 700,
        letterSpacing: 0.15,
        fontSize: overlay ? '0.7rem' : undefined,
        color: tag.color || '#fff',
        bgcolor: tag.backgroundColor || '#334155',
        border: overlay ? '1px solid rgba(255,255,255,0.35)' : 'none',
        boxShadow: overlay ? '0 2px 10px rgba(15,23,42,0.35)' : 'none',
        backdropFilter: overlay ? 'blur(6px)' : 'none',
        '& .MuiChip-icon': {
          color: 'inherit',
          ml: 0.75,
          fontSize: overlay ? 14 : 16,
        },
        '& .MuiChip-label': { px: 1.1, py: 0.1 },
      }}
    />
  );

  return (
    <Tooltip
      title={
        <Box sx={{ py: 0.25 }}>
          <Typography variant="caption" display="block" fontWeight={700}>
            {tag.label}
          </Typography>
          {description !== tag.label && (
            <Typography variant="caption" display="block" sx={{ opacity: 0.9, mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      {chip}
    </Tooltip>
  );
}

const EventTagChips: React.FC<EventTagChipsProps> = ({
  tags = [],
  size = 'small',
  max = 2,
  overlay = false,
  compact = false,
}) => {
  if (!tags.length) return null;
  const visible = tags.slice(0, max);

  const content = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {visible.map((tag) => (
        <TagChip key={tag.id} tag={tag} size={size} compact={compact || overlay} overlay={overlay} />
      ))}
    </Stack>
  );

  if (overlay) {
    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          px: 1.25,
          py: 1,
          background: 'linear-gradient(to top, rgba(15,23,42,0.72) 0%, rgba(15,23,42,0.35) 55%, transparent 100%)',
        }}
      >
        {content}
      </Box>
    );
  }

  return <Box sx={{ mb: 1.5 }}>{content}</Box>;
};

export default EventTagChips;
